import { useTranslation } from 'react-i18next';

import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { handleAuth } from 'store/reducers/auth.reducer';

import { useMutation } from '@tanstack/react-query';
import { emailLoginFinish, emailLoginStart, emailRegisterStart } from 'api';
import { AppRoutes } from 'config/routes';
import { EmailStartAuthRequest } from 'models/api/auth/email-start-auth-request';
import { useState } from 'react';

interface EmailAuthProps {
  email: string;
  password: string;
}

export default function useEmailLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);

  const { mutateAsync: startLogin } = useMutation((data: EmailStartAuthRequest) =>
    emailLoginStart(data),
  );

  const { mutateAsync: finishLogin } = useMutation((data: EmailStartAuthRequest) =>
    emailLoginFinish(data),
  );

  const { mutateAsync: startRegister } = useMutation((data: EmailStartAuthRequest) =>
    emailRegisterStart(data),
  );

  const login = async ({ email, password }: EmailAuthProps) => {
    const { Auth } = AppRoutes;

    try {
      const data = await startLogin({ login: email, password: '' });
      const { identity_exists } = data;

      if (!identity_exists) {
        const response = await startRegister({
          login: email,
          password,
        });
        navigate(`${Auth.Home + Auth.PinCode}?token=${response?.token}`, {
          state: { password, from: state?.from },
        });
        return;
      }

      setLoading(true);

      try {
        const result = await finishLogin({ login: email, password });
        dispatch(handleAuth(result));
        navigate(state?.from ?? '/');
      } catch (error: any) {
        // Wrong password / verification failed must stay on the form (not jump to register).
        message.error(error?.message || t('Something went wrong...'));
      }
    } catch (error: any) {
      message.error(error?.message || t('Something went wrong...'));
    } finally {
      setLoading(false);
    }
  };

  return { login, isLoading };
}
